// Chemin: C:\gestion-copro\src\types\document.types.ts
// Session 16 : Révision complète des types de documents
// Session 46 : Ajout types pour emails archivés
// Session 63 : Enrichissement GED pilotée par SENTINELLE
// Session 132 : Ajout donneesCles (Record<string, string>) dans Document, DocumentCreateData, DocumentUpdateData
//               Permet de persister les données factuelles extraites par SENTINELLE (montants, dates, statut paiement, etc.)
// Session 142 : INTELLIGENCE CORRESPONDANCE NIVEAU 1
// Session 232 : Ajout estArchive — documents archivés exclus du contexte SENTINELLE
//               Ajout champs correspondance dans SuggestionsSentinelle : courrierId_lie, courrierRef_lie,
//               typeCorrespondance, qualiteReponse, pointsTraites, pointsManquants, alerteQualite.
//               Ajout champs correspondance dans Document, DocumentCreateData, DocumentUpdateData.
//               Nouveau type TypeCorrespondance, QualiteReponse.

import { Timestamp } from 'firebase/firestore';
import {
  TypeDocumentGED,
  CategorieDocument,
  VisibiliteDocument,
  StatutSentinelle,
  TYPES_DOCUMENT_GED,
  CATEGORIES_DOCUMENT,
  VISIBILITE_PAR_DEFAUT,
} from '../constants/documentTaxonomy';

// ============================================
// RÉEXPORT DES TYPES TAXONOMIE
// ============================================

export type { TypeDocumentGED, CategorieDocument, VisibiliteDocument, StatutSentinelle };
export { TYPES_DOCUMENT_GED, CATEGORIES_DOCUMENT };

// ============================================
// SESSION 142 : TYPES CORRESPONDANCE
// ============================================

/**
 * Type de correspondance détecté par SENTINELLE
 * - reponse : le document est une réponse à un courrier envoyé
 * - relance : le document est une relance d'un échange précédent
 * - accuseReception : le document accuse réception d'un courrier
 */
export type TypeCorrespondance = 'reponse' | 'relance' | 'accuseReception';

/**
 * Qualité de la réponse évaluée par SENTINELLE
 * - complete : tous les points soulevés sont traités
 * - partielle : certains points traités, d'autres ignorés
 * - incomplete : aucun ou presque aucun point traité
 */
export type QualiteReponse = 'complete' | 'partielle' | 'incomplete';

// ============================================
// ANCIENS TYPES (conservation pour migration)
// À supprimer après migration complète
// ============================================

/**
 * @deprecated Utiliser TypeDocumentGED à la place
 * Conservé uniquement pour la migration des documents existants
 */
export type TypeDocumentLegacy = 
  | 'correspondance_entrante'
  | 'correspondance_sortante'
  | 'pv_compte_rendu'
  | 'rapport_diagnostic'
  | 'document_technique'
  | 'devis'
  | 'facture'
  | 'photo_video'
  | 'contrat_assurance'
  | 'document_administratif'
  | 'autre';

/**
 * @deprecated Utiliser TYPES_DOCUMENT_GED à la place
 */
export const TYPE_DOCUMENT_LABELS: Record<TypeDocumentLegacy, string> = {
  correspondance_entrante: 'Correspondance entrante',
  correspondance_sortante: 'Correspondance sortante',
  pv_compte_rendu: 'PV / Compte-rendu',
  rapport_diagnostic: 'Rapport / Diagnostic',
  document_technique: 'Document technique',
  devis: 'Devis',
  facture: 'Facture',
  photo_video: 'Photo / Vidéo',
  contrat_assurance: 'Contrat / Assurance',
  document_administratif: 'Document administratif',
  autre: 'Autre',
};

// Type union pour la période de transition
export type TypeDocument = TypeDocumentGED | TypeDocumentLegacy;

// ============================================
// MÉTADONNÉES EMAIL ARCHIVÉ
// ============================================

/**
 * Métadonnées d'un email archivé
 * Session 46 : Typage pour page détail document
 */
export interface SourceEmail {
  messageId: string;
  from: string;
  to: string;
  date: Timestamp;
  subject: string;
}

// ============================================
// BLOC SENTINELLE
// ============================================

/**
 * Suggestions originales de SENTINELLE lors de l'analyse
 * Session 142 : Ajout champs Intelligence Correspondance
 */
export interface SuggestionsSentinelle {
  type?: TypeDocumentGED;
  categorie?: CategorieDocument;
  nomFichier?: string;
  affaireId?: string;
  affaireNom?: string;
  resume?: string;
  dateDocument?: Timestamp;
  interlocuteurId?: string;
  interlocuteurNom?: string;
  confiance?: number; // Score de confiance 0-100

  // Session 142 : Intelligence Correspondance Niveau 1
  courrierId_lie?: string;            // ID Firestore du courrier d'origine
  courrierRef_lie?: string;           // Référence humaine (ex: "COU-2026-004")
  typeCorrespondance?: TypeCorrespondance;
  qualiteReponse?: QualiteReponse;
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string;
}

/**
 * Bloc SENTINELLE attaché au document
 * Trace l'analyse et la validation
 */
export interface DocumentSentinelle {
  // Statut de validation
  statut: StatutSentinelle;
  
  // Suggestions initiales de SENTINELLE
  suggestionsOriginales?: SuggestionsSentinelle;
  
  // Validation humaine
  validePar?: string;      // userId
  valideParNom?: string;   // Nom pour affichage
  valideAt?: Timestamp;
  
  // En cas de rejet
  motifRejet?: string;
  
  // En cas de correction
  champsModifies?: string[]; // Liste des champs corrigés par l'utilisateur
  
  // Métadonnées analyse
  analyseAt?: Timestamp;
  analyseVersion?: string; // Version du prompt SENTINELLE utilisé
}

// ============================================
// ENTITÉ DOCUMENT (enrichie)
// ============================================

/**
 * Entité principale Document
 * Représente un document archivé dans la GED de la copropriété
 * Session 63 : Enrichissement pour GED SENTINELLE
 * Session 132 : Ajout donneesCles
 * Session 142 : Ajout champs correspondance
 */
export interface Document {
  id: string;
  coproprieteId: string; // Clé de cloisonnement multi-tenant
  
  // === Informations descriptives ===
  titre: string;
  description?: string;
  
  // === Classification (Session 63) ===
  type: TypeDocument;              // Nouveau type GED ou legacy (migration)
  categorie?: CategorieDocument;   // NOUVEAU - Obligatoire pour nouveaux docs
  
  // === Fichier ===
  fichierUrl: string;              // URL Firebase Storage
  fichierNom: string;              // Nom original du fichier
  fichierTaille: number;           // Taille en octets
  fichierType: string;             // MIME type
  nomFichierNormalise?: string;    // NOUVEAU - Nom selon convention SENTINELLE
  
  // === Visibilité (Session 63) ===
  visibilite?: VisibiliteDocument; // NOUVEAU - 'cs' par défaut
  
  // === Liens avec autres entités ===
  interlocuteurId?: string;
  interlocuteurNom?: string;
  signalementIds?: string[];
  affaireId?: string;
  affaireNom?: string;             // NOUVEAU - Dénormalisé pour affichage
  
  // === Dates ===
  dateDocument: Timestamp;         // Date du document (pas d'upload)
  
  // === Traçabilité ===
  uploadePar: string;              // userId
  uploadeParNom: string;
  
  // === SENTINELLE (Session 63) ===
  sentinelle?: DocumentSentinelle; // NOUVEAU - Bloc analyse SENTINELLE
  resume?: string;                 // NOUVEAU - Résumé généré par SENTINELLE
  
  // === Données clés structurées (Session 132) ===
  // Données factuelles extraites par SENTINELLE lors de l'analyse.
  // Exploitées par le contextBuilder pour répondre aux questions utilisateur
  // (ex: montants, dates d'exigibilité, statut paiement, trimestre, etc.)
  donneesCles?: Record<string, string>;

  // === Intelligence Correspondance (Session 142) ===
  // Rattachement à un courrier existant détecté par SENTINELLE
  courrierId_lie?: string;            // ID Firestore du courrier d'origine
  courrierRef_lie?: string;           // Référence humaine (ex: "COU-2026-004")
  typeCorrespondance?: TypeCorrespondance;
  qualiteReponse?: QualiteReponse;
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string;
  
  // === Métadonnées ===
  createdAt: Timestamp;
  updatedAt: Timestamp;
  
  // === Archivage SENTINELLE (Session 232) ===
  // Quand true, le document est exclu du contexte SENTINELLE (ne fausse plus l'analyse)
  // Le document reste visible dans la GED avec badge "Archivé" et peut être désarchivé
  estArchive?: boolean;

  // === Champs spécifiques emails archivés (Session 46) ===
  sourceEmail?: SourceEmail;
  contenuTexte?: string;
  pieceJointes?: string[];
}

// ============================================
// DONNÉES CRÉATION / MISE À JOUR
// ============================================

/**
 * Données pour l'upload d'un nouveau document
 * Session 63 : Ajout champs GED
 * Session 132 : Ajout donneesCles
 * Session 142 : Ajout champs correspondance
 */
export interface DocumentCreateData {
  coproprieteId: string;
  titre: string;
  description?: string;
  
  // Classification
  type: TypeDocumentGED;           // Nouveau type obligatoire
  categorie: CategorieDocument;    // NOUVEAU - Obligatoire
  
  // Fichier
  fichierNom: string;
  fichierTaille: number;
  fichierType: string;
  
  // Visibilité
  visibilite?: VisibiliteDocument; // Défaut: 'cs'
  
  // Liens
  interlocuteurId?: string;
  interlocuteurNom?: string;
  signalementIds?: string[];
  affaireId?: string;
  affaireNom?: string;
  
  // Date
  dateDocument: Date;
  
  // Traçabilité
  uploadePar: string;
  uploadeParNom: string;
  
  // SENTINELLE
  sentinelle?: DocumentSentinelle;
  resume?: string;
  nomFichierNormalise?: string;
  
  // Session 132 : Données clés structurées extraites par SENTINELLE
  donneesCles?: Record<string, string>;

  // Session 142 : Correspondance
  courrierId_lie?: string;
  courrierRef_lie?: string;
  typeCorrespondance?: TypeCorrespondance;
  qualiteReponse?: QualiteReponse;
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string;

  // Session 232 : Archivage
  estArchive?: boolean;
}

/**
 * Données pour la mise à jour d'un document
 * Session 63 : Ajout champs GED
 * Session 132 : Ajout donneesCles
 * Session 142 : Ajout champs correspondance
 */
export interface DocumentUpdateData {
  titre?: string;
  description?: string;
  type?: TypeDocumentGED;
  categorie?: CategorieDocument;
  visibilite?: VisibiliteDocument;
  interlocuteurId?: string;
  interlocuteurNom?: string;
  signalementIds?: string[];
  affaireId?: string;
  affaireNom?: string;
  dateDocument?: Date;
  resume?: string;
  nomFichierNormalise?: string;
  sentinelle?: Partial<DocumentSentinelle>;
  // Session 132 : Données clés structurées
  donneesCles?: Record<string, string>;
  // Session 142 : Correspondance
  courrierId_lie?: string;
  courrierRef_lie?: string;
  typeCorrespondance?: TypeCorrespondance;
  qualiteReponse?: QualiteReponse;
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string;

  // Session 232 : Archivage
  estArchive?: boolean;
}

// ============================================
// FILTRES ET RECHERCHE
// ============================================

/**
 * Filtres pour la recherche de documents
 * Session 63 : Ajout filtres GED
 */
export interface DocumentFilters {
  type?: TypeDocumentGED;
  categorie?: CategorieDocument;   // NOUVEAU
  visibilite?: VisibiliteDocument; // NOUVEAU
  interlocuteurId?: string;
  signalementId?: string;
  affaireId?: string;
  dateDebut?: Date;
  dateFin?: Date;
  statutSentinelle?: StatutSentinelle; // NOUVEAU
  recherche?: string;              // NOUVEAU - Recherche texte libre
}

/**
 * Document avec métadonnées d'affichage enrichies
 */
export interface DocumentAvecDetails extends Document {
  signalementsTitres?: string[];
  affaireTitre?: string;
}

// ============================================
// VALIDATION FICHIER
// ============================================

/**
 * Résultat de validation d'un fichier avant upload
 */
export interface ValidationFichier {
  valide: boolean;
  erreur?: string;
  alertes?: string[];
  formatDetecte?: string;
  tailleNiveau?: 'ok' | 'alerte' | 'refuse';
}

// ============================================
// SUGGESTION DOCUMENT (workflow membre CS)
// ============================================

/**
 * Suggestion de document par un membre CS
 * Le Pilote GED valide ou rejette
 */
export interface SuggestionDocument {
  id: string;
  coproprieteId: string;
  
  // Qui suggère
  suggerePar: string;        // userId
  suggereParNom: string;
  suggereAt: Timestamp;
  
  // Fichier suggéré
  fichierUrl: string;
  fichierNom: string;
  fichierTaille: number;
  fichierType: string;
  
  // Message explicatif
  message: string;
  
  // Statut
  statut: 'en_attente' | 'accepte' | 'rejete';
  
  // Traitement par le Pilote GED
  traitePar?: string;
  traiteParNom?: string;
  traiteAt?: Timestamp;
  motifRejet?: string;
  documentId?: string;       // ID du document créé si accepté
}

// ============================================
// HISTORIQUE CORRECTIONS SENTINELLE
// ============================================

/**
 * Entrée d'historique des corrections SENTINELLE
 * Pour amélioration continue du modèle
 */
export interface CorrectionSentinelle {
  id: string;
  documentId: string;
  coproprieteId: string;
  
  // Ce que SENTINELLE avait suggéré
  suggestionOriginale: SuggestionsSentinelle;
  
  // Ce que l'utilisateur a choisi
  valeurFinale: {
    type?: TypeDocumentGED;
    categorie?: CategorieDocument;
    nomFichier?: string;
    affaireId?: string;
  };
  
  // Métadonnées
  corrigePar: string;
  corrigeParNom: string;
  corrigeAt: Timestamp;
}

// ============================================
// CONSTANTES PAR DÉFAUT
// ============================================

export const DOCUMENT_DEFAULTS = {
  visibilite: VISIBILITE_PAR_DEFAUT,
  categorie: 'administratif' as CategorieDocument,
  statutSentinelle: 'en_attente' as StatutSentinelle,
} as const;
